import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIsSidekickSseEnabled } from '../flag/useIsSidekickSseEnabled';
import {
  addErrorEvent,
  removeErrorEvent,
  replaceErrorEvent,
  selectConversationScopeVersion,
  setIsStreaming,
  setRetryAttempt,
} from '../state/aiSidekickChatSlice';
import type { RetryAttempt, SidekickError } from '../state/chatTypes';
import { classifySidekickErrorPresentation } from '../state/errorClassification';
import { buildErrorEvent } from './useConversation.helpers';
import {
  type ConversationTransportTurn,
  getRetryDelayMs,
  MAX_TRANSPORT_ATTEMPTS,
  runConversationTransportTurn,
  scheduleAfterTransportCleanup,
} from './useConversation.transport.helpers';
import { useConversationRetryState } from './useConversationRetryState';
import type {
  ActiveTransportExecution,
  UseConversationTransportParams,
} from './useConversationTransport.types';
import { useConversationTransportScope } from './useConversationTransportScope';
import { useSidekickEventStream } from './useSidekickEventStream';
import { useSidekickSyncResponse } from './useSidekickSyncResponse';

/** Owns transport selection, automatic attempts, and retry completion state. */
export const useConversationTransport = ({
  activeConversationId,
  apiBaseUrl,
  onNetworkError,
}: UseConversationTransportParams) => {
  const dispatch = useDispatch();
  const conversationScopeVersion = useSelector(selectConversationScopeVersion);
  const isSseEnabled = useIsSidekickSseEnabled();
  const onNetworkErrorRef = useRef(onNetworkError);
  onNetworkErrorRef.current = onNetworkError;
  const retryAttemptRef = useRef<RetryAttempt>(0);
  const currentTransportExecutionRef = useRef<ActiveTransportExecution | null>(
    null
  );
  const runTransportTurnRef = useRef<
    ((turn: ConversationTransportTurn) => Promise<void>) | null
  >(null);
  const abortEventStreamRef = useRef<() => void>(() => undefined);
  const abortSyncResponseRef = useRef<() => void>(() => undefined);
  const scheduledRetryCleanupRef = useRef<(() => void) | null>(null);
  const {
    activateRetry,
    cancelRetryReservation,
    clearActiveRetryState,
    reserveRetry,
  } = useConversationRetryState();
  const clearScheduledRetry = useCallback((): void => {
    scheduledRetryCleanupRef.current?.();
    scheduledRetryCleanupRef.current = null;
  }, []);
  const endExecution = useCallback((): void => {
    clearScheduledRetry();
    retryAttemptRef.current = 0;
    currentTransportExecutionRef.current = null;
  }, [clearScheduledRetry]);
  const clearTransportState = useCallback((): void => {
    abortEventStreamRef.current();
    abortSyncResponseRef.current();
    endExecution();
    clearActiveRetryState();
  }, [clearActiveRetryState, endExecution]);
  const { captureScope, isExecutionCurrent } = useConversationTransportScope({
    activeConversationId,
    clearTransportState,
    conversationScopeVersion,
    currentTransportExecutionRef,
  });

  const notifyNetworkError = useCallback((): void => {
    onNetworkErrorRef.current?.();
  }, []);
  const handleTransportError = useCallback(
    (error: SidekickError): boolean => {
      const currentExecution = currentTransportExecutionRef.current;
      if (currentExecution === null || !isExecutionCurrent(currentExecution)) {
        return false;
      }
      const presentation = classifySidekickErrorPresentation(error);
      if (presentation.surface === 'toast') {
        endExecution();
        dispatch(setIsStreaming(false));
        clearActiveRetryState();
        notifyNetworkError();
        return false;
      }
      if (
        presentation.canRetry &&
        retryAttemptRef.current < MAX_TRANSPORT_ATTEMPTS
      ) {
        const nextAttempt = (retryAttemptRef.current + 1) as RetryAttempt;
        retryAttemptRef.current = nextAttempt;
        dispatch(setRetryAttempt(nextAttempt));
        clearScheduledRetry();
        scheduledRetryCleanupRef.current = scheduleAfterTransportCleanup({
          delayMs: getRetryDelayMs(presentation.retryAfterSeconds),
          onWaiting: () => {
            if (isExecutionCurrent(currentExecution)) {
              dispatch(setIsStreaming(true));
            }
          },
          callback: () => {
            scheduledRetryCleanupRef.current = null;
            if (isExecutionCurrent(currentExecution)) {
              void runTransportTurnRef.current?.(currentExecution.turn);
            }
          },
        });
        return true;
      }
      endExecution();
      dispatch(setIsStreaming(false));
      const errorEvent = buildErrorEvent(
        currentExecution.sourceMessageId,
        error,
        {
          fleetId: currentExecution.turn.fleetId,
          interfaceName: currentExecution.turn.interfaceName,
        }
      );
      dispatch(
        currentExecution.retryErrorId === null
          ? addErrorEvent(errorEvent)
          : replaceErrorEvent({
              ...errorEvent,
              id: currentExecution.retryErrorId,
            })
      );
      clearActiveRetryState();
      return false;
    },
    [
      clearActiveRetryState,
      clearScheduledRetry,
      dispatch,
      endExecution,
      isExecutionCurrent,
      notifyNetworkError,
    ]
  );
  const handleTransportDone = useCallback((): void => {
    const completedExecution = currentTransportExecutionRef.current;
    if (
      completedExecution === null ||
      !isExecutionCurrent(completedExecution)
    ) {
      return;
    }
    endExecution();
    if (completedExecution.retryErrorId !== null) {
      dispatch(removeErrorEvent(completedExecution.retryErrorId));
    }
    clearActiveRetryState();
  }, [clearActiveRetryState, dispatch, endExecution, isExecutionCurrent]);

  const eventStream = useSidekickEventStream({
    conversationId: activeConversationId,
    apiBaseUrl,
    onError: handleTransportError,
    onDone: handleTransportDone,
  });
  const syncResponse = useSidekickSyncResponse({
    conversationId: activeConversationId,
    apiBaseUrl,
    onError: handleTransportError,
    onDone: handleTransportDone,
  });
  abortEventStreamRef.current = eventStream.abort;
  abortSyncResponseRef.current = syncResponse.abort;
  const runTransportTurn = useCallback(
    (turn: ConversationTransportTurn): Promise<void> =>
      runConversationTransportTurn({
        turn,
        isSseEnabled,
        eventStream,
        syncResponse,
      }),
    [eventStream, isSseEnabled, syncResponse]
  );
  runTransportTurnRef.current = runTransportTurn;

  const beginSubmission = useCallback(
    (retryErrorId: string | null = null): void => {
      clearScheduledRetry();
      if (retryErrorId === null) {
        clearActiveRetryState();
      }
      retryAttemptRef.current = 1;
      dispatch(setRetryAttempt(1));
      dispatch(setIsStreaming(true));
    },
    [clearActiveRetryState, clearScheduledRetry, dispatch]
  );

  // Ends the running turn here and drops any pending retry. Resolves once the
  // question is acknowledged, when the backend can be asked to stop the turn.
  const stopTurn = useCallback((): Promise<void> => {
    const stopping = eventStream.stop();
    abortSyncResponseRef.current();
    endExecution();
    clearActiveRetryState();
    dispatch(setIsStreaming(false));
    return stopping;
  }, [clearActiveRetryState, dispatch, endExecution, eventStream]);

  const cancelSubmission = useCallback((): void => {
    dispatch(setIsStreaming(false));
    clearActiveRetryState();
  }, [clearActiveRetryState, dispatch]);

  const runTurn = useCallback(
    async (
      sourceMessageId: string,
      turn: ConversationTransportTurn,
      retryErrorId: string | null = null
    ): Promise<void> => {
      currentTransportExecutionRef.current = {
        sourceMessageId,
        turn,
        retryErrorId,
        scope: captureScope(),
      };
      await runTransportTurn(turn);
    },
    [captureScope, runTransportTurn]
  );

  return {
    activateRetry,
    beginSubmission,
    cancelRetryReservation,
    cancelSubmission,
    notifyNetworkError,
    reserveRetry,
    runTurn,
    stopTurn,
  };
};
